import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ArrowLeft, Search, Droplets, User, MapPin, 
  Calendar, CreditCard, Check, Loader2, AlertCircle,
  Receipt, History, CheckCircle2, Share2, Printer, Lock, Banknote, MessageCircle,
  Users, X as IconX, ChevronRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import axios from "axios";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import LocationSaver from "@/components/LocationSaver";
import { buildWhatsappUrl } from "@/lib/waUrl";
import { printReceiptSmart } from "@/lib/btPrinter";
import { PaymentDisabledBanner } from "@/components/PaymentGate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Logo size for receipts
const LOGO_MAX_SIZE = 80;

/**
 * Preload image and ensure it's ready for html2canvas
 */
const preloadImage = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
};

/**
 * Wait for all images in element to load
 */
const waitForImagesToLoad = async (element) => {
  const images = element.querySelectorAll('img');
  const promises = Array.from(images).map(img => {
    if (img.complete && img.naturalHeight !== 0) return Promise.resolve();
    return new Promise((resolve) => {
      img.onload = resolve;
      img.onerror = resolve;
      setTimeout(resolve, 3000); // Timeout fallback
    });
  });
  await Promise.all(promises);
  await new Promise(r => setTimeout(r, 300)); // Extra delay
};

export default function PDAMTebo() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const autoSearchedRef = useRef(false);
  const [noSambungan, setNoSambungan] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [pelanggan, setPelanggan] = useState(null);
  const [bills, setBills] = useState([]);
  const [localPaidBills, setLocalPaidBills] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedBills, setSelectedBills] = useState([]);
  const [paymentResult, setPaymentResult] = useState(null);
  const [adminFee, setAdminFee] = useState(2500);
  // Toggle biaya admin (untuk petugas di kantor: bisa dimatikan → admin = 0)
  const [adminEnabled, setAdminEnabled] = useState(true);
  const [isSharing, setIsSharing] = useState(false);
  const [serviceEnabled, setServiceEnabled] = useState(true);
  const [checkingService, setCheckingService] = useState(true);
  const [receiptSettings, setReceiptSettings] = useState(null);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [uangDiterima, setUangDiterima] = useState("");
  const [kembalian, setKembalian] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(""); // "cash" or "transfer"
  const [paymentMethodError, setPaymentMethodError] = useState("");
  
  // WhatsApp Template states
  const [waTemplates, setWaTemplates] = useState([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [requireLogin, setRequireLogin] = useState(true);
  // Saved WA number per current pelanggan (from customer_locations)
  const [savedWaNumber, setSavedWaNumber] = useState("");
  // WhatsApp app preference: "ask" (system default), "wa" (regular), "wab" (business)
  const [waAppPref, setWaAppPref] = useState(() => localStorage.getItem("wa_app_pref") || "ask");
  const [showWaAppPicker, setShowWaAppPicker] = useState(false);

  const persistWaAppPref = (val) => {
    setWaAppPref(val);
    localStorage.setItem("wa_app_pref", val);
  };

  // WhatsApp app URL helper imported from @/lib/waUrl — reads waAppPref from localStorage.

  // Multi-search states
  const [showMultiSearchModal, setShowMultiSearchModal] = useState(false);
  const [multiSearchInput, setMultiSearchInput] = useState("");
  const [multiResults, setMultiResults] = useState([]); // [{ pelanggan, bills, error }]
  const [multiLoading, setMultiLoading] = useState(false);
  const [selectedMultiIndex, setSelectedMultiIndex] = useState(0);
  
  const receiptRef = useRef(null);
  const billsRef = useRef(null);
  
  // Check if user is logged in and their role
  // Support both user_token (phone login) and customer_token (Google Auth)
  const userToken = localStorage.getItem("user_token");
  const customerToken = localStorage.getItem("customer_token");
  const authToken = userToken || customerToken;
  
  // Get user info from appropriate storage
  const userName = userToken 
    ? localStorage.getItem("user_name") 
    : localStorage.getItem("customer_name");
  const userRole = userToken 
    ? (localStorage.getItem("user_role") || "") 
    : (localStorage.getItem("customer_role") || "pelanggan");
  const isLoggedIn = !!authToken;
  
  // Only petugas/kasir can select specific bills (mode angsuran)
  const isPetugas = (userRole.toLowerCase() === "petugas" || userRole.toLowerCase() === "kasir") && isLoggedIn;
  const isKasir = userRole.toLowerCase() === "kasir" && isLoggedIn;
  const isPetugasLapangan = userRole.toLowerCase() === "petugas_lapangan" && isLoggedIn;
  // Read-only mode: petugas_lapangan cannot pay
  const isReadOnly = isPetugasLapangan;

  // Check service status on mount
  useEffect(() => {
    const checkServiceStatus = async () => {
      try {
        const [statusRes, receiptRes, templatesRes, authSettingsRes] = await Promise.all([
          axios.get(`${API}/service-status`),
          axios.get(`${API}/receipt-settings`),
          axios.get(`${API}/wa-templates`),
          axios.get(`${API}/auth-settings`)
        ]);
        setServiceEnabled(statusRes.data.pdam_tebo !== false);
        setReceiptSettings(receiptRes.data);
        setWaTemplates(templatesRes.data.templates || []);
        setRequireLogin(authSettingsRes.data.require_login_for_bills !== false);
      } catch (err) {
        console.error("Failed to check service status:", err);
      } finally {
        setCheckingService(false);
      }
    };
    checkServiceStatus();
  }, []);

  // Kasir: tidak dikenakan biaya admin
  useEffect(() => {
    if (isKasir) setAdminFee(0);
  }, [isKasir]);

  // Fetch saved WhatsApp number for current pelanggan
  useEffect(() => {
    const fetchSavedWa = async () => {
      if (!pelanggan?.no_sambungan || !authToken) {
        setSavedWaNumber("");
        return;
      }
      try {
        // Sumber utama: pdam_tebo_master.nomor_telp (ditulis oleh LocationSaver).
        const res = await axios.get(
          `${API}/peta-global/customer/${encodeURIComponent(pelanggan.no_sambungan)}`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        let wa = res.data?.nomor_telp || "";
        // Fallback data lama: koleksi customer_locations.
        if (!wa) {
          try {
            const res2 = await axios.get(
              `${API}/customer-locations/${encodeURIComponent(pelanggan.no_sambungan)}`,
              { headers: { Authorization: `Bearer ${authToken}` } }
            );
            wa = res2.data?.location?.whatsapp_number || "";
          } catch { /* ignore */ }
        }
        setSavedWaNumber(wa);
      } catch {
        setSavedWaNumber("");
      }
    };
    fetchSavedWa();
  }, [pelanggan, authToken]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const handleSearch = async () => {
    if (!serviceEnabled) {
      toast.error("Layanan sedang dalam perbaikan server. Silakan coba lagi nanti.");
      return;
    }
    
    // Check if login is required
    if (requireLogin && !isLoggedIn) {
      toast.error("Silakan login terlebih dahulu untuk melihat tagihan");
      navigate("/login");
      return;
    }
    
    if (!noSambungan.trim()) {
      toast.error("Masukkan nomor sambungan");
      return;
    }
    
    setIsLoading(true);
    setPelanggan(null);
    setBills([]);
    setLocalPaidBills([]);
    setSelectedBills([]);
    setPaymentResult(null);
    
    try {
      // Include auth header if logged in (for wilayah validation)
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
      
      const response = await axios.post(`${API}/pdam-tebo/check`, {
        no_sambungan: noSambungan.trim(),
        nama: ""  // Empty string to search by no_sambungan only
      }, { headers });
      
      if (response.data.success) {
        setPelanggan(response.data.pelanggan);
        setBills(response.data.bills);
        setLocalPaidBills(response.data.local_paid_bills || []);
        
        if (response.data.bills.length === 0) {
          toast.success("Tidak ada tagihan yang belum dibayar");
        } else {
          toast.success(`Ditemukan ${response.data.bills.length} tagihan`);
        }
      }
    } catch (err) {
      console.error("Error:", err);
      toast.error(err.response?.data?.detail || "Gagal mengambil data tagihan");
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // Riwayat pembayaran via aplikasi — always fetched fresh from pdam_tebo_payments,
  // so it stays in sync with Kelola Transaksi (deleted there = gone here).
  const loadPaymentHistory = useCallback(async (noSamb) => {
    try {
      const res = await axios.get(`${API}/pdam-tebo/history/${encodeURIComponent(noSamb)}`);
      setPaymentHistory(res.data?.payments || []);
    } catch {
      setPaymentHistory([]);
    }
  }, []);

  useEffect(() => {
    const ns = pelanggan?.no_sambungan;
    setShowHistory(false);
    setPaymentHistory([]);
    if (ns) loadPaymentHistory(ns);
  }, [pelanggan?.no_sambungan, loadPaymentHistory]);

  // Deep-link support: open /pdam-tebo?id=<customerId> from Peta Global "Bayar" button.
  // Pre-fill the connection number and auto-trigger the bill check once ready.
  useEffect(() => {
    const pid = (searchParams.get("id") || "").replace(/[^0-9]/g, "");
    if (pid && !noSambungan) setNoSambungan(pid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    const pid = (searchParams.get("id") || "").replace(/[^0-9]/g, "");
    if (!pid || autoSearchedRef.current) return;
    if (checkingService) return;          // wait for service/auth settings to load
    if (!noSambungan) return;             // wait until field is populated
    autoSearchedRef.current = true;
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, checkingService, noSambungan]);

  // ====== MULTI SEARCH ======
  const parseMultiIds = (raw) => {
    if (!raw) return [];
    // Accept newlines, commas, semicolons, spaces as separators
    return [...new Set(
      raw.split(/[\s,;\n]+/).map((s) => s.replace(/[^0-9]/g, "").trim()).filter((s) => s.length > 0)
    )];
  };

  const handleMultiSearch = async () => {
    if (requireLogin && !isLoggedIn) {
      toast.error("Silakan login terlebih dahulu");
      navigate("/login");
      return;
    }
    const ids = parseMultiIds(multiSearchInput);
    if (ids.length === 0) {
      toast.error("Masukkan minimal 1 nomor sambungan.");
      return;
    }
    setShowMultiSearchModal(false);
    setMultiLoading(true);
    // Clear single result
    setPelanggan(null);
    setBills([]);
    setLocalPaidBills([]);
    setSelectedBills([]);
    setPaymentResult(null);
    setMultiResults([]);
    setSelectedMultiIndex(0);

    const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
    toast.info(`Mengambil data ${ids.length} pelanggan...`);

    // Fetch in parallel (but limit batch size to avoid overwhelming)
    const batchSize = 5;
    const results = [];
    for (let i = 0; i < ids.length; i += batchSize) {
      const batch = ids.slice(i, i + batchSize);
      const batchRes = await Promise.all(
        batch.map(async (id) => {
          try {
            const res = await axios.post(`${API}/pdam-tebo/check`, { no_sambungan: id, nama: "" }, { headers });
            if (res.data.success) {
              return { id, pelanggan: res.data.pelanggan, bills: res.data.bills, error: null };
            }
            return { id, pelanggan: null, bills: [], error: "Tidak ditemukan" };
          } catch (err) {
            return { id, pelanggan: null, bills: [], error: err.response?.data?.detail || "Gagal" };
          }
        })
      );
      results.push(...batchRes);
    }

    setMultiResults(results);
    setMultiLoading(false);

    // Pick the first valid result as initial selection
    const firstValid = results.findIndex((r) => r.pelanggan);
    if (firstValid >= 0) {
      setSelectedMultiIndex(firstValid);
      setPelanggan(results[firstValid].pelanggan);
      setBills(results[firstValid].bills);
    }
    const okCount = results.filter((r) => r.pelanggan).length;
    toast.success(`${okCount} dari ${ids.length} pelanggan ditemukan.`);
  };

  const selectMultiResult = (index) => {
    const r = multiResults[index];
    if (!r?.pelanggan) {
      toast.error(r?.error || "Pelanggan tidak valid.");
      return;
    }
    setSelectedMultiIndex(index);
    setPelanggan(r.pelanggan);
    setBills(r.bills);
    setSelectedBills([]);
    setPaymentResult(null);
    // Scroll detail into view (smooth)
    setTimeout(() => {
      const el = document.getElementById("multi-detail-anchor");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const clearMultiSearch = () => {
    setMultiResults([]);
    setSelectedMultiIndex(0);
    setPelanggan(null);
    setBills([]);
    setSelectedBills([]);
    setPaymentResult(null);
    setMultiSearchInput("");
  };

  const toggleBillSelection = (bill) => {
    // Only petugas can select individual bills
    if (!isPetugas) return;
    
    setSelectedBills(prev => {
      const isSelected = prev.find(b => b.id === bill.id);
      if (isSelected) {
        return prev.filter(b => b.id !== bill.id);
      } else {
        return [...prev, bill];
      }
    });
  };

  const selectAll = () => {
    // Only petugas can select/deselect
    if (!isPetugas) return;
    
    if (selectedBills.length === bills.length) {
      setSelectedBills([]);
    } else {
      setSelectedBills([...bills]);
    }
  };

  // For petugas: use selected bills; for others: use all bills
  const billsToPay = isPetugas ? selectedBills : bills;

  // For share (Share Tagihan / Share WA): if petugas has explicit selection, share only that subset (angsuran).
  // Otherwise share all unpaid bills. This keeps WA message & shared image in sync with the checkboxes.
  const billsToShare = (isPetugas && selectedBills.length > 0) ? selectedBills : bills;
  const isPartialShare = isPetugas && selectedBills.length > 0 && selectedBills.length < bills.length;

  // Biaya admin efektif: 0 untuk kasir, atau saat toggle admin dimatikan (petugas di kantor)
  const effectiveAdminFee = (isKasir || !adminEnabled) ? 0 : adminFee;

  const calculateTotals = () => {
    const totalTagihan = billsToPay.reduce((sum, b) => sum + b.total, 0);
    const totalAdminFee = billsToPay.length * effectiveAdminFee;
    const totalBayar = totalTagihan + totalAdminFee;
    return { totalTagihan, totalAdminFee, totalBayar };
  };

  const handlePayment = async () => {
    if (!isLoggedIn) {
      toast.error("Silakan login terlebih dahulu");
      navigate("/login");
      return;
    }
    
    if (billsToPay.length === 0) {
      toast.error(isPetugas ? "Pilih tagihan yang akan dibayar" : "Tidak ada tagihan");
      return;
    }
    
    // Open password confirmation dialog
    setPassword("");
    setPasswordError("");
    setUangDiterima("");
    setKembalian(null);
    setPaymentMethod("");
    setPaymentMethodError("");
    setShowPasswordDialog(true);
  };

  const handleConfirmPayment = async () => {
    // Validate payment method (required)
    if (!paymentMethod) {
      setPaymentMethodError("Pilih metode pembayaran");
      return;
    }
    
    // For Google Auth users (Pelanggan), skip password verification
    // Only verify password for phone/password login users
    const isGoogleAuth = !!customerToken && !userToken;
    
    // Password required only for phone/password login users
    if (!isGoogleAuth && !password.trim()) {
      setPasswordError("Masukkan password");
      return;
    }

    setIsProcessing(true);
    setPasswordError("");
    setPaymentMethodError("");
    
    if (!isGoogleAuth) {
      try {
        // Verify password first
        await axios.post(
          `${API}/auth/verify-password`,
          { password: password },
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
      } catch (err) {
        setPasswordError(err.response?.data?.detail || "Password salah");
        setIsProcessing(false);
        return;
      }
    }
    
    // Calculate kembalian
    const { totalBayar: totalBayarCalc } = calculateTotals();
    const uangDiterimaNum = parseInt(uangDiterima) || 0;
    const kembalianCalc = uangDiterimaNum > 0 ? uangDiterimaNum - totalBayarCalc : null;
    
    try {
      const response = await axios.post(
        `${API}/pdam-tebo/pay-batch`,
        {
          no_sambungan: pelanggan.no_sambungan,
          nama_pelanggan: pelanggan.nama,
          alamat: pelanggan.alamat,
          bills: billsToPay.map(bill => ({
            periode: bill.periode,
            periode_text: bill.periode_text,
            total: bill.total,
            detail: bill.detail || null
          })),
          admin_fee: effectiveAdminFee,
          payment_method: paymentMethod  // "cash" atau "transfer"
        },
        {
          headers: { Authorization: `Bearer ${authToken}` }
        }
      );
      
      setPaymentResult({
        ...response.data.payment,
        pelanggan,
        timestamp: new Date().toLocaleString("id-ID"),
        payment_method: paymentMethod,  // "cash" atau "transfer"
        uang_diterima: uangDiterimaNum > 0 ? uangDiterimaNum : null,

        kembalian: kembalianCalc !== null && kembalianCalc >= 0 ? kembalianCalc : null
      });
      
      setShowPasswordDialog(false);
      toast.success(`${billsToPay.length} tagihan berhasil dibayar`);
      loadPaymentHistory(pelanggan.no_sambungan);
      
    } catch (err) {
      console.error("Payment error:", err);
      toast.error(err.response?.data?.detail || "Terjadi kesalahan saat memproses pembayaran");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleShare = async () => {
    if (!receiptRef.current) return;
    
    setIsSharing(true);
    toast.info("Menyiapkan struk...");
    
    try {
      // Preload logo if exists
      if (receiptSettings?.logo_url) {
        try {
          await preloadImage(receiptSettings.logo_url);
        } catch (e) {
          console.warn('Failed to preload logo:', e);
        }
      }
      
      // Wait for all images to load
      await waitForImagesToLoad(receiptRef.current);
      
      const canvas = await html2canvas(receiptRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
        imageTimeout: 0
      });
      
      const blob = await new Promise((resolve) => {
        canvas.toBlob(resolve, 'image/jpeg', 0.9);
      });
      
      const fileName = `Struk_PDAM_Tebo_${pelanggan?.no_sambungan || ''}_${Date.now()}.jpg`;
      const file = new File([blob], fileName, { type: 'image/jpeg' });
      
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: `Struk Pembayaran PDAM Tebo`,
          files: [file],
        });
        toast.success("Struk berhasil dibagikan!");
      } else {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        toast.success("Struk berhasil diunduh!");
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("Share error:", err);
        toast.error("Gagal membuat struk");
      }
    } finally {
      setIsSharing(false);
    }
  };

  // Generate plain text receipt for thermal printer (58mm)
  const generateReceiptText = () => {
    if (!paymentResult) return "";
    const divider = "--------------------------------";
    const formatRp = (n) => `Rp${(n || 0).toLocaleString('id-ID')}`;
    const headerText = receiptSettings?.header_text || "PRODUK DIGITAL";
    const showFee = receiptSettings?.show_fee_loket !== false;
    const footerText = receiptSettings?.footer_text || "Terima kasih atas pembayaran Anda";
    
    let receipt = "";
    receipt += `     ${headerText}\n`;
    receipt += "     Struk Pembayaran\n";
    receipt += divider + "\n";
    receipt += `No.Trx: ${paymentResult.batch_id?.slice(0,8).toUpperCase()}\n`;
    receipt += `Tgl   : ${paymentResult.tanggal || paymentResult.timestamp}\n`;
    receipt += divider + "\n";
    receipt += `No.Samb  : ${paymentResult.no_sambungan}\n`;
    receipt += `Nama     : ${paymentResult.nama}\n`;
    if (receiptSettings?.show_customer_address !== false && paymentResult.alamat) {
      receipt += `Alamat   : ${paymentResult.alamat}\n`;
    }
    receipt += divider + "\n";
    
    // List each bill
    paymentResult.results?.forEach((result) => {
      receipt += `Periode  : ${result.periode}\n`;
      // Detail Stan Meter
      if (result.detail) {
        receipt += `Stan Lalu: ${result.detail.stan_lalu?.toLocaleString('id-ID') || '-'}\n`;
        receipt += `Stan Skrg: ${result.detail.stan_sekarang?.toLocaleString('id-ID') || '-'}\n`;
        receipt += `Pakai    : ${result.detail.pakai?.toLocaleString('id-ID') || '-'} m3\n`;
      }
      receipt += `SUBTOTAL : ${formatRp(result.total_bayar)}\n`;
      receipt += divider + "\n";
    });
    
    // Show Total Tagihan if show_total_tagihan is enabled (default true)
    if (receiptSettings?.show_total_tagihan !== false) {
      receipt += `Total Tagihan : ${formatRp(paymentResult.total_tagihan)}\n`;
      if (showFee) {
        receipt += `Total Admin   : ${formatRp(paymentResult.total_admin)}\n`;
      }
    }
    // TOTAL BAYAR selalu ditampilkan
    receipt += `TOTAL BAYAR   : ${formatRp(paymentResult.grand_total)}\n`;
    receipt += divider + "\n";
    
    // Uang diterima & kembalian
    if (paymentResult.uang_diterima) {
      receipt += `Uang Diterima : ${formatRp(paymentResult.uang_diterima)}\n`;
      receipt += `Kembalian     : ${formatRp(paymentResult.kembalian || 0)}\n`;
      receipt += divider + "\n";
    }
    
    receipt += `Status : LUNAS\n`;
    if (receiptSettings?.show_kasir !== false) {
      receipt += `Kasir  : ${paymentResult.kasir}\n`;
    }
    receipt += divider + "\n";
    receipt += `${footerText}\n`;
    receipt += `        STRUK INI ADALAH\n`;
    receipt += `      PEMBAYARAN YANG SAH\n`;
    receipt += `        by:produk_digital\n`;
    receipt += "\n\n\n";
    
    return receipt;
  };

  // Print via RawBT app (for Bluetooth thermal printer)
  // Uses plain text format (logo only for Share function)
  const handlePrint = async () => {
    if (!paymentResult) return;
    await printReceiptSmart(generateReceiptText(), toast);
  };

  // Open template selection modal for share
  const openShareTemplateModal = () => {
    if (waTemplates.length > 0) {
      setSelectedTemplate(null);
      setShowTemplateModal(true);
    } else {
      // No templates available, share directly without text
      handleShareBillsWithTemplate(null);
    }
  };

  // Replace placeholders in template with actual data
  const replaceTemplatePlaceholders = (template) => {
    if (!template || !pelanggan) return "";
    
    let content = template.content;
    // Use billsToShare so template reflects the selected bills (angsuran) instead of all unpaid bills
    const list = billsToShare;
    const totalTagihan = list.reduce((sum, b) => sum + b.total, 0);
    const totalBayar = totalTagihan + (list.length * effectiveAdminFee);
    const periodeList = list.map(b => b.periode_text).join(", ");
    
    // Replace placeholders
    content = content.replace(/{nama_pelanggan}/g, pelanggan.nama || "");
    content = content.replace(/{id_pelanggan}/g, pelanggan.no_sambungan || "");
    content = content.replace(/{alamat}/g, pelanggan.alamat || "");
    content = content.replace(/{jumlah_tagihan}/g, `${list.length} bulan`);
    content = content.replace(/{total_tagihan}/g, formatCurrency(totalBayar));
    content = content.replace(/{periode}/g, periodeList);
    
    return content;
  };

  // Share bills/tagihan result with optional template
  const handleShareBillsWithTemplate = async (template) => {
    if (!billsRef.current) return;
    
    setShowTemplateModal(false);
    setIsSharing(true);
    toast.info("Menyiapkan tagihan...");
    
    try {
      // Temporarily make the hidden div visible for capture
      const element = billsRef.current;
      const originalStyle = element.style.cssText;
      element.style.cssText = 'position: fixed; top: 0; left: 0; z-index: -1; opacity: 1; width: 400px;';
      
      // Wait for images to load
      await waitForImagesToLoad(element);
      
      const canvas = await html2canvas(element, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
        width: 400,
        windowWidth: 400,
        imageTimeout: 0
      });
      
      // Restore original style
      element.style.cssText = originalStyle;
      
      const blob = await new Promise((resolve) => {
        canvas.toBlob(resolve, 'image/jpeg', 0.9);
      });
      
      const fileName = `Tagihan_PDAM_Tebo_${pelanggan?.no_sambungan || ''}_${Date.now()}.jpg`;
      const file = new File([blob], fileName, { type: 'image/jpeg' });
      
      // Prepare template text if template is selected
      const templateText = template ? replaceTemplatePlaceholders(template) : "";

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: `Tagihan PDAM Tebo - ${pelanggan?.nama || ''}`,
          text: templateText,
          files: [file],
        });
        toast.success("Tagihan berhasil dibagikan!");
      } else {
        // Fallback: download image and copy text to clipboard
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        if (templateText) {
          try {
            await navigator.clipboard.writeText(templateText);
            toast.success("Tagihan diunduh! Teks template sudah disalin.");
          } catch {
            toast.success("Tagihan berhasil diunduh!");
          }
        } else {
          toast.success("Tagihan berhasil diunduh!");
        }
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("Share bills error:", err);
        toast.error("Gagal membagikan tagihan");
      }
    } finally {
      setIsSharing(false);
    }
  };

  // Generate the bill card (billsRef) as a JPEG image File
  const generateBillImageFile = async () => {
    if (!billsRef.current) return null;
    const element = billsRef.current;
    const originalStyle = element.style.cssText;
    element.style.cssText = 'position: fixed; top: 0; left: 0; z-index: -1; opacity: 1; width: 400px;';
    await waitForImagesToLoad(element);
    const canvas = await html2canvas(element, {
      backgroundColor: '#ffffff',
      scale: 2,
      useCORS: true,
      allowTaint: true,
      logging: false,
      width: 400,
      windowWidth: 400,
      imageTimeout: 0,
    });
    element.style.cssText = originalStyle;
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
    if (!blob) return null;
    const fileName = `Tagihan_PDAM_Tebo_${pelanggan?.no_sambungan || ''}_${Date.now()}.jpg`;
    return { file: new File([blob], fileName, { type: 'image/jpeg' }), blob, fileName };
  };

  // Tombol "Share Tagihan": buka modal pilih Template WA, lalu share GAMBAR + teks template
  const handleShareBills = async () => {
    openShareTemplateModal();
  };

  // ====== Share Tagihan WhatsApp (direct to saved WA number) ======
  const [shareWaMode, setShareWaMode] = useState(false); // when true, template modal will route to WhatsApp directly

  const openShareWhatsappFlow = () => {
    if (!savedWaNumber) {
      toast.error("Mohon simpan No. WhatsApp pelanggan terlebih dahulu.", { duration: 4000 });
      return;
    }
    // First time: ask which WhatsApp app to use (only matters on Android with both apps installed)
    if (waAppPref === "ask") {
      setShowWaAppPicker(true);
      return;
    }
    proceedShareWhatsappFlow();
  };

  const proceedShareWhatsappFlow = () => {
    setShareWaMode(true);
    if (waTemplates.length > 0) {
      setSelectedTemplate(null);
      setShowTemplateModal(true);
    } else {
      handleShareToWhatsapp(null);
    }
  };

  const handleShareToWhatsapp = async (template) => {
    if (!savedWaNumber) {
      toast.error("Mohon simpan No. WhatsApp pelanggan terlebih dahulu.");
      setShowTemplateModal(false);
      setShareWaMode(false);
      return;
    }

    setShowTemplateModal(false);
    setIsSharing(true);
    toast.info("Menyiapkan tagihan...");

    const templateText = template ? replaceTemplatePlaceholders(template) : "";

    try {
      const gen = await generateBillImageFile();

      // UTAMA: lampirkan GAMBAR + teks template sekaligus (foto 1 + caption salam).
      if (gen && navigator.canShare && navigator.canShare({ files: [gen.file] })) {
        try {
          await navigator.share({
            files: [gen.file],
            text: templateText || undefined,
            title: `Tagihan PDAM Tebo - ${pelanggan?.nama || ''}`,
          });
          toast.success("Gambar tagihan siap dibagikan ke WhatsApp!");
          return;
        } catch (err) {
          if (err.name === "AbortError") return;
          // selain dibatalkan user → jatuh ke fallback wa.me di bawah
        }
      }

      // FALLBACK (perangkat tak mendukung share file): unduh gambar + buka wa.me
      // ke nomor tersimpan dengan teks template, lalu user lampirkan gambar manual.
      let imageSaved = false;
      if (gen) {
        const url = URL.createObjectURL(gen.blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = gen.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 4000);
        imageSaved = true;
      }

      const waUrl = buildWhatsappUrl(savedWaNumber, templateText);
      try {
        const a = document.createElement("a");
        a.href = waUrl;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch (e) {
        try { window.location.href = waUrl; } catch (_) { /* ignore */ }
      }

      toast.success(
        imageSaved
          ? `Gambar tagihan disimpan ke galeri/Download. Buka WhatsApp → +${savedWaNumber}, lampirkan gambar dari galeri.`
          : `Membuka WhatsApp ke +${savedWaNumber}.`,
        { duration: 7000 }
      );
    } catch (err) {
      console.warn("Share to WhatsApp failed:", err);
      toast.error("Gagal membagikan tagihan");
    } finally {
      setIsSharing(false);
      setShareWaMode(false);
    }
  };

  const resetAll = () => {
    setPaymentResult(null);
    setSelectedBills([]);
    setPelanggan(null);
    setBills([]);
    setNoSambungan("");
    setShowPasswordDialog(false);
    setPassword("");
    setPasswordError("");
    setUangDiterima("");
    setKembalian(null);
  };

  const { totalTagihan, totalAdminFee, totalBayar } = calculateTotals();

  // Show loading while checking service status
  if (checkingService) {
    return (
      <div className="min-h-screen hero-gradient flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  // Show maintenance message if service is disabled
  if (!serviceEnabled) {
    return (
      <div className="min-h-screen hero-gradient">
        <header className="container mx-auto px-6 py-6">
          <nav className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Droplets className="w-8 h-8 text-cyan-600" />
              <span className="text-xl font-heading font-semibold text-slate-800">PDAM Tirta Muaro Tebo</span>
            </div>
            <Link to="/">
              <Button variant="ghost" size="sm" className="gap-2">
                <ArrowLeft className="w-4 h-4" />
                Kembali
              </Button>
            </Link>
          </nav>
        </header>
        <main className="container mx-auto px-6 py-8">
          <div className="max-w-md mx-auto text-center">
            <Card className="glass-card-solid">
              <CardContent className="p-12">
                <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
                <h2 className="text-xl font-heading font-semibold text-slate-800 mb-2">
                  Sedang Perbaikan Server
                </h2>
                <p className="text-slate-600 mb-6">
                  Layanan PDAM Tebo Online sedang dalam perbaikan. Silakan coba lagi nanti atau gunakan layanan tagihan PDAM biasa.
                </p>
                <Link to="/">
                  <Button className="gap-2 bg-gradient-to-r from-cyan-500 to-blue-600">
                    <ArrowLeft className="w-4 h-4" />
                    Kembali ke Beranda
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen hero-gradient">
      <header className="container mx-auto px-6 py-6">
        <nav className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Droplets className="w-8 h-8 text-cyan-600" />
            <span className="text-xl font-heading font-semibold text-slate-800">PDAM Tirta Muaro Tebo</span>
          </div>
          <Link to="/">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Kembali
            </Button>
          </Link>
        </nav>
      </header>

      <main className="container mx-auto px-6 py-8">
        <div className="max-w-3xl mx-auto mb-4"><PaymentDisabledBanner /></div>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="max-w-2xl mx-auto">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Droplets className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900 mb-2">
                PDAM Tirta Muaro Tebo
              </h1>
              <p className="text-slate-600">
                Cek dan bayar tagihan PDAM Kabupaten Tebo
              </p>
              {isLoggedIn && (
                <p className="text-sm text-sky-600 mt-2">
                  Login sebagai: <span className="font-semibold">{userName}</span>
                </p>
              )}
            </div>

            {/* Payment Result / Receipt */}
            <AnimatePresence>
              {paymentResult && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                >
                  <Card className="glass-card-solid mb-6 overflow-hidden">
                    <div ref={receiptRef} className="bg-white">
                      <div className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white p-4 text-center">
                        {/* Logo - tampilkan logo upload jika ada, jika tidak tampilkan icon */}
                        {receiptSettings?.logo_url ? (
                          <div className="flex justify-center mb-2">
                            <img 
                              src={receiptSettings.logo_url} 
                              alt="Logo" 
                              style={{
                                maxWidth: `${LOGO_MAX_SIZE}px`,
                                maxHeight: `${LOGO_MAX_SIZE}px`,
                                width: 'auto',
                                height: 'auto',
                                objectFit: 'contain',
                                borderRadius: '8px',
                                backgroundColor: 'white',
                                padding: '4px'
                              }}
                            />
                          </div>
                        ) : (
                          <Droplets className="w-10 h-10 mx-auto mb-2" />
                        )}
                        <h2 className="text-lg font-bold" data-testid="receipt-header">
                          {receiptSettings?.header_text || "PRODUK DIGITAL"}
                        </h2>
                        <p className="text-sm opacity-90">Struk Pembayaran</p>
                      </div>
                      
                      <CardContent className="p-4">
                        {/* Transaction info */}
                        <div className="border-b border-dashed border-slate-200 pb-3 mb-3">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-slate-500">No. Transaksi</span>
                            <span className="font-mono font-semibold text-slate-800">{paymentResult.batch_id?.slice(0,8).toUpperCase()}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-500">Tanggal</span>
                            <span className="font-medium text-slate-800">{paymentResult.tanggal || paymentResult.timestamp}</span>
                          </div>
                        </div>
                        
                        {/* Customer info */}
                        <div className="border-b border-dashed border-slate-200 pb-3 mb-3">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-slate-500">No. Sambungan</span>
                            <span className="font-mono font-semibold">{paymentResult.no_sambungan}</span>
                          </div>
                          <p className="font-semibold text-slate-800">{paymentResult.nama}</p>
                          {receiptSettings?.show_customer_address !== false && paymentResult.alamat && (
                            <p className="text-xs text-slate-500">{paymentResult.alamat}</p>
                          )}
                        </div>
                        
                        {/* Bill details per period */}
                        <div className="space-y-3 mb-4">
                          {paymentResult.results?.map((result, idx) => (
                            <div key={idx} className="border border-slate-200 rounded-lg p-3" data-testid={`receipt-bill-${idx}`}>
                              <div className="flex items-center justify-between mb-2">
                                <div>
                                  <p className="text-xs text-slate-500">Periode</p>
                                  <p className="font-semibold text-slate-800">{result.periode}</p>
                                </div>
                                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">LUNAS</span>
                              </div>
                              
                              {/* Detail Stan Meter */}
                              {result.detail && (
                                <div className="grid grid-cols-3 gap-1 text-xs mb-2">
                                  <div className="bg-slate-50 rounded p-1.5 text-center">
                                    <p className="text-slate-500">Stan Lalu</p>
                                    <p className="font-semibold text-slate-700">{result.detail.stan_lalu?.toLocaleString('id-ID') || '-'}</p>
                                  </div>
                                  <div className="bg-slate-50 rounded p-1.5 text-center">
                                    <p className="text-slate-500">Stan Skrg</p>
                                    <p className="font-semibold text-slate-700">{result.detail.stan_sekarang?.toLocaleString('id-ID') || '-'}</p>
                                  </div>
                                  <div className="bg-cyan-50 rounded p-1.5 text-center">
                                    <p className="text-cyan-600">Pakai</p>
                                    <p className="font-bold text-cyan-700">{result.detail.pakai?.toLocaleString('id-ID') || '-'} m³</p>
                                  </div>
                                </div>
                              )}
                              
                              <div className="space-y-1 text-sm">
                                <div className="flex justify-between pt-1 border-t border-slate-100 font-bold">
                                  <span className="text-slate-700 text-base">Subtotal</span>
                                  <span className="text-cyan-600 text-base">{formatCurrency(result.total_bayar)}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        {/* Grand Total */}
                        <div className="bg-slate-50 rounded-xl p-3">
                          {receiptSettings?.show_total_tagihan !== false && (
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-slate-600">Total Tagihan</span>
                              <span className="font-medium">{formatCurrency(paymentResult.total_tagihan)}</span>
                            </div>
                          )}
                          {receiptSettings?.show_fee_loket !== false && (paymentResult.total_admin > 0) && (
                            <div className="flex justify-between text-sm mb-2">
                              <span className="text-slate-600">Total Biaya Admin ({paymentResult.jumlah_bulan || paymentResult.results?.length} x {formatCurrency(paymentResult.results?.[0]?.admin_fee || 0)})</span>
                              <span className="font-medium">{formatCurrency(paymentResult.total_admin)}</span>
                            </div>
                          )}
                          <div className="flex justify-between items-center pt-2 border-t border-slate-200">
                            <span className="font-bold text-slate-800">Total Bayar</span>
                            <span className="text-xl font-bold text-cyan-600">
                              {formatCurrency(paymentResult.grand_total)}
                            </span>
                          </div>
                        </div>
                        
                        {/* Status and Kasir */}
                        <div className="mt-3 pt-3 border-t border-dashed border-slate-200">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-slate-500">Status</span>
                            <span className="font-semibold text-green-600 flex items-center gap-1">
                              LUNAS <CheckCircle2 className="w-4 h-4" />
                            </span>
                          </div>
                          {receiptSettings?.show_kasir !== false && paymentResult.kasir && (
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-500">Kasir</span>
                              <span className="font-semibold text-slate-800">
                                {paymentResult.kasir}
                              </span>
                            </div>
                          )}
                          {paymentResult.uang_diterima && (
                            <>
                              <div className="flex justify-between text-sm mt-2">
                                <span className="text-slate-500">Uang Diterima</span>
                                <span className="font-medium text-slate-800">{formatCurrency(paymentResult.uang_diterima)}</span>
                              </div>
                              <div className="flex justify-between text-sm mt-1">
                                <span className="text-slate-500 font-semibold">Kembalian</span>
                                <span className="font-bold text-green-600">{formatCurrency(paymentResult.kembalian || 0)}</span>
                              </div>
                            </>
                          )}
                        </div>
                        
                        <p className="text-center text-xs text-slate-400 mt-4">
                          {receiptSettings?.footer_text || "Terima kasih atas pembayaran Anda"}
                        </p>
                        <p className="text-center text-xs text-slate-400">
                          {receiptSettings?.note_text || "Struk ini adalah bukti pembayaran yang sah"}
                        </p>
                      </CardContent>
                    </div>
                    
                    <div className="p-4 bg-slate-50 space-y-3">
                      <div className="flex gap-3">
                        <Button 
                          variant="outline" 
                          className="flex-1 gap-2"
                          onClick={handlePrint}
                          disabled={isSharing}
                          data-testid="print-receipt-btn"
                        >
                          {isSharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
                          Print Struk
                        </Button>
                        <Button 
                          variant="outline" 
                          className="flex-1 gap-2"
                          onClick={handleShare}
                          disabled={isSharing}
                          data-testid="share-receipt-btn"
                        >
                          {isSharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                          Share Struk
                        </Button>
                      </div>
                      <Button 
                        className="w-full gap-2 bg-gradient-to-r from-cyan-500 to-blue-600"
                        onClick={resetAll}
                        data-testid="new-search-btn"
                      >
                        <Search className="w-4 h-4" />
                        Cek Tagihan Lain
                      </Button>
                      {searchParams.get("from") === "peta" && (
                        <Button
                          variant="outline"
                          className="w-full gap-2 border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                          onClick={() => navigate(`/peta-global?focus=${encodeURIComponent(searchParams.get("focus") || searchParams.get("id") || "")}`)}
                          data-testid="paid-back-to-peta-btn"
                        >
                          <MapPin className="w-4 h-4" />
                          Kembali ke Peta (lokasi tadi)
                        </Button>
                      )}
                    </div>
                  </Card>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Payment Confirmation Dialog */}
            <AnimatePresence>
              {showPasswordDialog && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
                  onClick={(e) => { if (e.target === e.currentTarget && !isProcessing) setShowPasswordDialog(false); }}
                >
                  <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden"
                  >
                    <div className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white p-4 text-center">
                      <Lock className="w-8 h-8 mx-auto mb-1" />
                      <h3 className="font-bold text-lg">Konfirmasi Pembayaran</h3>
                      <p className="text-sm opacity-90">
                        {customerToken && !userToken 
                          ? "Konfirmasi untuk melanjutkan pembayaran" 
                          : "Masukkan password untuk melanjutkan"}
                      </p>
                    </div>
                    <div className="p-5 space-y-4">
                      {/* Payment summary */}
                      <div className="bg-slate-50 rounded-xl p-3 text-sm space-y-1">
                        <div className="flex justify-between">
                          <span className="text-slate-600">Pelanggan PDAM</span>
                          <span className="font-semibold">{pelanggan?.nama}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Jumlah Tagihan</span>
                          <span className="font-semibold">{billsToPay.length} bulan</span>
                        </div>
                        <div className="flex justify-between border-t border-slate-200 pt-1 mt-1">
                          <span className="font-semibold text-slate-800">Total Bayar</span>
                          <span className="font-bold text-cyan-600">{formatCurrency(totalBayar)}</span>
                        </div>
                      </div>

                      {/* Payment Method Selection - REQUIRED */}
                      <div>
                        <label className="text-sm font-medium text-slate-700 mb-2 block">
                          Metode Pembayaran <span className="text-red-500">*</span>
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            type="button"
                            onClick={() => { setPaymentMethod("cash"); setPaymentMethodError(""); }}
                            className={`flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all ${
                              paymentMethod === "cash" 
                                ? "border-emerald-500 bg-emerald-50 text-emerald-700" 
                                : "border-slate-200 bg-white text-slate-600 hover:border-emerald-300"
                            }`}
                            data-testid="tebo-payment-method-cash"
                            disabled={isProcessing}
                          >
                            <Banknote className="w-5 h-5" />
                            <span className="font-semibold">Cash</span>
                            {paymentMethod === "cash" && <Check className="w-4 h-4" />}
                          </button>
                          <button
                            type="button"
                            onClick={() => { setPaymentMethod("transfer"); setPaymentMethodError(""); }}
                            className={`flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all ${
                              paymentMethod === "transfer" 
                                ? "border-blue-500 bg-blue-50 text-blue-700" 
                                : "border-slate-200 bg-white text-slate-600 hover:border-blue-300"
                            }`}
                            data-testid="tebo-payment-method-transfer"
                            disabled={isProcessing}
                          >
                            <CreditCard className="w-5 h-5" />
                            <span className="font-semibold">Transfer</span>
                            {paymentMethod === "transfer" && <Check className="w-4 h-4" />}
                          </button>
                        </div>
                        {paymentMethodError && (
                          <p className="text-red-500 text-xs mt-1" data-testid="tebo-payment-method-error">{paymentMethodError}</p>
                        )}
                      </div>

                      {/* Password input - Only show for phone/password login users */}
                      {userToken && (
                        <div>
                          <label className="text-sm font-medium text-slate-700 mb-1 block">Password</label>
                          <Input
                            type="password"
                            placeholder="Masukkan password Anda"
                            value={password}
                            onChange={(e) => { setPassword(e.target.value); setPasswordError(""); }}
                            onKeyDown={(e) => { if (e.key === "Enter") handleConfirmPayment(); }}
                            className={`h-12 rounded-xl ${passwordError ? "border-red-400" : ""}`}
                            data-testid="password-confirm-input"
                            disabled={isProcessing}
                            autoFocus
                          />
                          {passwordError && (
                            <p className="text-red-500 text-xs mt-1" data-testid="password-error">{passwordError}</p>
                          )}
                        </div>
                      )}

                      {/* Google Auth user info */}
                      {customerToken && !userToken && (
                        <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl">
                          <p className="text-sm text-blue-800">
                            Pembayaran akan menggunakan saldo akun <strong>{userName}</strong>
                          </p>
                        </div>
                      )}

                      {/* Uang Diterima input - Only show for Petugas/Agen */}
                      {userToken && (
                        <div>
                          <label className="text-sm font-medium text-slate-700 mb-1 block">
                            <Banknote className="w-4 h-4 inline mr-1" />
                            Uang Diterima (opsional)
                          </label>
                          <Input
                            type="tel"
                            inputMode="numeric"
                            placeholder="Masukkan jumlah uang dari pelanggan"
                            value={uangDiterima ? parseInt(uangDiterima).toLocaleString('id-ID') : ''}
                            onChange={(e) => {
                              const val = e.target.value.replace(/[^0-9]/g, '');
                              setUangDiterima(val);
                            }}
                            className="h-12 rounded-xl"
                            data-testid="uang-diterima-input"
                            disabled={isProcessing}
                          />
                          {uangDiterima && parseInt(uangDiterima) > 0 && (
                            <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                              <div className="flex justify-between text-sm">
                                <span className="text-green-700">Kembalian</span>
                                <span className={`font-bold ${parseInt(uangDiterima) >= totalBayar ? 'text-green-600' : 'text-red-600'}`}>
                                  {parseInt(uangDiterima) >= totalBayar
                                    ? formatCurrency(parseInt(uangDiterima) - totalBayar)
                                    : `Kurang ${formatCurrency(totalBayar - parseInt(uangDiterima))}`
                                  }
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Action buttons */}
                      <div className="flex gap-3 pt-2">
                        <Button
                          variant="outline"
                          className="flex-1 h-12 rounded-xl"
                          onClick={() => setShowPasswordDialog(false)}
                          disabled={isProcessing}
                          data-testid="cancel-payment-btn"
                        >
                          Batal
                        </Button>
                        <Button
                          className="flex-1 h-12 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600"
                          onClick={handleConfirmPayment}
                          disabled={isProcessing || (userToken && !password.trim())}
                          data-testid="confirm-payment-btn"
                        >
                          {isProcessing ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin mr-2" />
                              Memproses...
                            </>
                          ) : (
                            "Bayar Sekarang"
                          )}
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Template Selection Modal for Share */}
            <AnimatePresence>
              {showTemplateModal && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
                  onClick={(e) => { if (e.target === e.currentTarget) { setShowTemplateModal(false); setShareWaMode(false); } }}
                >
                  <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden max-h-[85vh] flex flex-col"
                  >
                    <div className={`text-white p-4 text-center ${shareWaMode ? "bg-[#25D366]" : "bg-gradient-to-r from-green-500 to-emerald-600"}`}>
                      {shareWaMode ? <MessageCircle className="w-8 h-8 mx-auto mb-1" /> : <Share2 className="w-8 h-8 mx-auto mb-1" />}
                      <h3 className="font-bold text-lg">{shareWaMode ? "Kirim ke WhatsApp Pelanggan" : "Pilih Template Pesan"}</h3>
                      <p className="text-sm opacity-90">
                        {shareWaMode
                          ? `Akan dikirim ke +${savedWaNumber}`
                          : "Template akan otomatis terisi saat share ke WhatsApp"}
                      </p>
                    </div>
                    <div className="p-4 space-y-3 overflow-y-auto flex-1">
                      {/* Option: No Template */}
                      <button
                        onClick={() => shareWaMode ? handleShareToWhatsapp(null) : handleShareBillsWithTemplate(null)}
                        className={`w-full p-4 border-2 rounded-xl text-left transition-all hover:border-green-300 hover:bg-green-50 ${
                          selectedTemplate === null ? 'border-green-500 bg-green-50' : 'border-slate-200'
                        }`}
                        data-testid="share-no-template"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                            <Share2 className="w-5 h-5 text-slate-500" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-slate-800">Tanpa Template</h4>
                            <p className="text-xs text-slate-500">Share gambar tagihan saja</p>
                          </div>
                        </div>
                      </button>

                      {/* Template Options */}
                      {waTemplates.map((template) => (
                        <button
                          key={template.template_id}
                          onClick={() => shareWaMode ? handleShareToWhatsapp(template) : handleShareBillsWithTemplate(template)}
                          className="w-full p-4 border-2 border-slate-200 rounded-xl text-left transition-all hover:border-green-300 hover:bg-green-50"
                          data-testid={`share-template-${template.template_id}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                              <MessageCircle className="w-5 h-5 text-green-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-slate-800">{template.name}</h4>
                              <p className="text-xs text-slate-500 line-clamp-2 mt-1">
                                {replaceTemplatePlaceholders(template)}
                              </p>
                            </div>
                          </div>
                        </button>
                      ))}

                      {waTemplates.length === 0 && (
                        <div className="text-center py-6">
                          <MessageCircle className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                          <p className="text-slate-500 text-sm">Belum ada template tersedia</p>
                          <p className="text-slate-400 text-xs">Admin dapat membuat template di Pengaturan</p>
                        </div>
                      )}
                    </div>
                    <div className="p-4 border-t border-slate-200 bg-slate-50">
                      <Button
                        variant="outline"
                        className="w-full h-11"
                        onClick={() => { setShowTemplateModal(false); setShareWaMode(false); }}
                      >
                        Batal
                      </Button>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {searchParams.get("from") === "tunggakan" && (
              <Button
                variant="outline"
                className="mb-4 gap-2 border-rose-300 text-rose-700 hover:bg-rose-50"
                onClick={() => navigate(searchParams.get("list") ? `/tunggakan/${searchParams.get("list")}` : "/tunggakan")}
                data-testid="back-to-tunggakan-btn"
              >
                <ArrowLeft className="w-4 h-4" /> Kembali ke Pindai Tunggakan
              </Button>
            )}

            {searchParams.get("from") === "peta" && (
              <Button
                variant="outline"
                className="mb-4 gap-2 border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                onClick={() => navigate(`/peta-global?focus=${encodeURIComponent(searchParams.get("focus") || searchParams.get("id") || "")}`)}
                data-testid="back-to-peta-btn"
              >
                <ArrowLeft className="w-4 h-4" /> Kembali ke Peta (lokasi tadi)
              </Button>
            )}

            {/* Search Form */}
            {!paymentResult && (
              <Card className="glass-card-solid mb-6" data-testid="search-card">
                <CardContent className="p-6">
                  {/* Top row: Multi search trigger */}
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs font-medium text-slate-500">
                      {multiResults.length > 0 ? `Multi: ${multiResults.length} ID dimasukkan` : "Cek tagihan PDAM Tebo"}
                    </p>
                    <div className="flex gap-2">
                      {multiResults.length > 0 && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-xs text-slate-500 hover:text-red-600 gap-1"
                          onClick={clearMultiSearch}
                          data-testid="clear-multi-btn"
                        >
                          <IconX className="w-3.5 h-3.5" /> Reset
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs gap-1 border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                        onClick={() => { setMultiSearchInput(""); setShowMultiSearchModal(true); }}
                        data-testid="open-multi-search-btn"
                      >
                        <Users className="w-3.5 h-3.5" /> Multi Pencarian
                      </Button>
                      {isPetugas && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs gap-1 border-blue-300 text-blue-700 hover:bg-blue-50"
                          onClick={() => navigate("/peta-global")}
                          data-testid="open-peta-global-btn"
                          title="Buka peta global semua pelanggan"
                        >
                          🌐 Peta Global
                        </Button>
                      )}
                      {isPetugas && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs gap-1 border-amber-300 text-amber-700 hover:bg-amber-50"
                          onClick={() => navigate("/antar-surat")}
                          data-testid="open-antar-surat-btn"
                          title="Daftar antar surat tunggakan"
                        >
                          ✉️ Antar Surat
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Input
                      type="tel"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      placeholder="Masukkan Nomor Sambungan (contoh: 02150012)"
                      value={noSambungan}
                      onChange={(e) => {
                        // Only allow numbers
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        setNoSambungan(value);
                      }}
                      onKeyPress={handleKeyPress}
                      className="flex-1 h-12 rounded-xl"
                      data-testid="no-sambungan-input"
                    />
                    <Button
                      onClick={handleSearch}
                      disabled={isLoading}
                      className="h-12 px-6 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700"
                      data-testid="search-btn"
                    >
                      {isLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Search className="w-5 h-5" />
                      )}
                    </Button>
                  </div>
                  
                  {!isLoggedIn && (
                    <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                      <p className="text-sm text-amber-800">
                        <strong>Info:</strong> Anda perlu <Link to="/login" className="text-amber-600 underline font-semibold">login</Link> untuk melakukan pembayaran
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Multi Search Modal */}
            <AnimatePresence>
              {showMultiSearchModal && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4"
                  onClick={() => setShowMultiSearchModal(false)}
                  data-testid="multi-search-modal"
                >
                  <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white p-4 text-center">
                      <Users className="w-8 h-8 mx-auto mb-1" />
                      <h3 className="font-bold">Multi Pencarian</h3>
                      <p className="text-xs opacity-90">Masukkan banyak ID sekaligus (tanpa batas). Pisahkan dengan baris baru, koma, atau spasi.</p>
                    </div>
                    <div className="p-4 space-y-3">
                      <textarea
                        value={multiSearchInput}
                        onChange={(e) => setMultiSearchInput(e.target.value)}
                        placeholder={"Contoh:\n02080095\n02150012\n02080100, 02080101"}
                        rows={8}
                        className="w-full text-sm font-mono border border-slate-200 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-cyan-400"
                        autoFocus
                        data-testid="multi-search-input"
                      />
                      <p className="text-xs text-slate-500">
                        Total ID terdeteksi: <span className="font-bold text-cyan-600">{parseMultiIds(multiSearchInput).length}</span>
                      </p>
                      <div className="flex gap-2">
                        <Button variant="outline" className="flex-1" onClick={() => setShowMultiSearchModal(false)} data-testid="cancel-multi-search">
                          Batal
                        </Button>
                        <Button
                          className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white"
                          onClick={handleMultiSearch}
                          disabled={parseMultiIds(multiSearchInput).length === 0}
                          data-testid="submit-multi-search"
                        >
                          <Search className="w-4 h-4 mr-1" /> Cari Semua
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* WhatsApp App Picker Modal */}
            <AnimatePresence>
              {showWaAppPicker && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4"
                  onClick={() => setShowWaAppPicker(false)}
                  data-testid="wa-app-picker-modal"
                >
                  <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="bg-[#25D366] text-white p-4 text-center">
                      <MessageCircle className="w-8 h-8 mx-auto mb-1" />
                      <h3 className="font-bold">Pilih Aplikasi WhatsApp</h3>
                      <p className="text-xs opacity-90">Buka chat pelanggan via aplikasi yang mana?</p>
                    </div>
                    <div className="p-4 space-y-2">
                      <button
                        type="button"
                        onClick={() => {
                          persistWaAppPref("wa");
                          setShowWaAppPicker(false);
                          if (savedWaNumber) proceedShareWhatsappFlow();
                        }}
                        className={`w-full p-3 border-2 rounded-xl text-left transition-all hover:bg-green-50 ${
                          waAppPref === "wa" ? "border-[#25D366] bg-green-50" : "border-slate-200"
                        }`}
                        data-testid="pick-wa-regular"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#25D366]/10 flex items-center justify-center">
                            <MessageCircle className="w-5 h-5 text-[#128C7E]" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-slate-800">WhatsApp</h4>
                            <p className="text-[11px] text-slate-500">WA pribadi (com.whatsapp)</p>
                          </div>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          persistWaAppPref("wab");
                          setShowWaAppPicker(false);
                          if (savedWaNumber) proceedShareWhatsappFlow();
                        }}
                        className={`w-full p-3 border-2 rounded-xl text-left transition-all hover:bg-emerald-50 ${
                          waAppPref === "wab" ? "border-[#075E54] bg-emerald-50" : "border-slate-200"
                        }`}
                        data-testid="pick-wa-business"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#075E54]/10 flex items-center justify-center">
                            <MessageCircle className="w-5 h-5 text-[#075E54]" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-slate-800">WhatsApp Business</h4>
                            <p className="text-[11px] text-slate-500">WA Business (com.whatsapp.w4b)</p>
                          </div>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          persistWaAppPref("ask");
                          setShowWaAppPicker(false);
                          toast.info("Akan tanya Android setiap kali (default sistem).");
                        }}
                        className="w-full p-2 text-xs text-slate-500 hover:text-slate-700"
                        data-testid="pick-wa-ask"
                      >
                        atau biarkan Android pilih (tidak target spesifik)
                      </button>
                      <p className="text-[10px] text-amber-700 italic text-center pt-1 border-t border-slate-100">
                        Pilihan disimpan di device ini. Bisa diubah lagi via link "Ubah" di bawah tombol Share.
                      </p>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Multi search results strip */}
            {multiResults.length > 0 && !paymentResult && (
              <Card className="glass-card-solid mb-4 border-2 border-cyan-100" data-testid="multi-results-strip">
                <CardContent className="p-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-semibold text-slate-700">
                      Hasil ({multiResults.filter((r) => r.pelanggan).length}/{multiResults.length})
                    </p>
                    <p className="text-[11px] text-slate-500">Tap kartu untuk lihat detail</p>
                  </div>
                  <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory" data-testid="multi-cards-scroll">
                    {multiResults.map((r, idx) => {
                      const active = idx === selectedMultiIndex && !!r.pelanggan;
                      const billsCount = r.bills?.length || 0;
                      const total = r.bills?.reduce((s, b) => s + (b.total || 0), 0) || 0;
                      return (
                        <button
                          key={`${r.id}-${idx}`}
                          type="button"
                          onClick={() => r.pelanggan && selectMultiResult(idx)}
                          className={`flex-shrink-0 snap-start text-left rounded-xl border-2 px-3 py-2 min-w-[200px] max-w-[220px] transition-all ${
                            r.pelanggan
                              ? active
                                ? "border-cyan-500 bg-cyan-50 shadow-md"
                                : "border-slate-200 bg-white hover:border-cyan-300"
                              : "border-red-200 bg-red-50 cursor-not-allowed opacity-70"
                          }`}
                          data-testid={`multi-card-${idx}`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-[10px] font-mono text-slate-500 truncate">{r.id}</span>
                            {r.pelanggan ? (
                              <ChevronRight className={`w-3.5 h-3.5 ${active ? "text-cyan-600" : "text-slate-400"}`} />
                            ) : (
                              <IconX className="w-3.5 h-3.5 text-red-400" />
                            )}
                          </div>
                          {r.pelanggan ? (
                            <>
                              <p className="font-semibold text-slate-800 text-sm truncate mt-0.5">{r.pelanggan.nama}</p>
                              <p className="text-[10px] text-slate-500 truncate">{r.pelanggan.alamat}</p>
                              <div className="flex items-center justify-between mt-1">
                                <span className="text-[10px] text-slate-500">{billsCount} tagihan</span>
                                <span className="text-xs font-bold text-cyan-600">{formatCurrency(total)}</span>
                              </div>
                            </>
                          ) : (
                            <p className="text-xs text-red-600 mt-1">{r.error || "Tidak ditemukan"}</p>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
            <div id="multi-detail-anchor" />

            {/* Loading State - Skeleton for faster perceived loading */}
            {isLoading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-4"
              >
                {/* Customer Info Skeleton */}
                <Card className="glass-card-solid animate-pulse">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-slate-200" />
                      <div className="flex-1 space-y-2">
                        <div className="h-5 bg-slate-200 rounded w-1/3" />
                        <div className="h-4 bg-slate-200 rounded w-2/3" />
                        <div className="flex gap-2 mt-3">
                          <div className="h-6 bg-slate-200 rounded-full w-20" />
                          <div className="h-6 bg-slate-200 rounded-full w-16" />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                {/* Loading indicator */}
                <div className="flex items-center justify-center py-4">
                  <div className="flex items-center gap-3 text-cyan-600">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="text-sm font-medium">Mengambil data tagihan...</span>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Customer Info and Bills for Share */}
            {!isLoading && pelanggan && !paymentResult && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
              >
                {/* Hidden div for share - contains customer info + bills */}
                <div ref={billsRef} className="bg-white rounded-2xl p-4" style={{ position: 'fixed', left: '-9999px', top: '0', width: '400px', zIndex: '-1' }}>
                  {/* Header for share image */}
                  <div className="text-center mb-4 pb-3 border-b border-dashed border-slate-200">
                    <Droplets className="w-8 h-8 text-cyan-600 mx-auto mb-2" />
                    <h2 className="font-heading font-bold text-cyan-700">PDAM Tirta Muaro Tebo</h2>
                    <p className="text-xs text-slate-500">Info Tagihan Pelanggan</p>
                  </div>
                  
                  {/* Customer Info for share */}
                  <div className="mb-4 p-4 border border-cyan-200 rounded-xl">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-cyan-100 flex items-center justify-center flex-shrink-0">
                        <User className="w-6 h-6 text-cyan-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-heading font-bold text-lg text-slate-800">
                          {pelanggan.nama}
                        </h3>
                        <p className="text-sm text-slate-600 flex items-center gap-1 mt-1">
                          <MapPin className="w-4 h-4" />
                          {pelanggan.alamat}
                        </p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          <span className="px-2 py-0.5 bg-slate-100 rounded-full text-xs font-medium text-slate-600">
                            ID: {pelanggan.no_sambungan}
                          </span>
                          <span className="px-2 py-0.5 bg-cyan-100 rounded-full text-xs font-medium text-cyan-700">
                            {pelanggan.golongan}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Bills List for share */}
                  {billsToShare.length > 0 && (
                    <>
                      <div className="mb-2">
                        <h2 className="font-heading font-semibold text-slate-800 text-sm">
                          {isPartialShare ? `Tagihan Dipilih (${billsToShare.length})` : `Tagihan Belum Dibayar (${billsToShare.length})`}
                        </h2>
                      </div>
                      <div className="space-y-2">
                        {billsToShare.map((bill) => (
                          <div 
                            key={bill.id}
                            className="border border-slate-200 rounded-lg p-3 bg-white"
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="flex items-center gap-2">
                                  <Calendar className="w-4 h-4 text-slate-400" />
                                  <span className="font-semibold text-slate-800 text-sm">{bill.periode_text}</span>
                                </div>
                                <p className="text-xs text-slate-500 mt-0.5">
                                  Periode: {bill.periode}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-cyan-600">
                                  {formatCurrency(bill.total)}
                                </p>
                                {effectiveAdminFee > 0 && (
                                  <p className="text-xs text-slate-500">
                                    + Admin {formatCurrency(effectiveAdminFee)}
                                  </p>
                                )}
                              </div>
                            </div>
                            {/* Detail Stan Meter for share */}
                            {bill.detail && (
                              <div className="mt-2 pt-2 border-t border-slate-100">
                                <div className="grid grid-cols-3 gap-1 text-xs">
                                  <div className="bg-slate-50 rounded p-1.5 text-center">
                                    <p className="text-slate-500">Stan Lalu</p>
                                    <p className="font-semibold text-slate-700">{bill.detail.stan_lalu?.toLocaleString('id-ID') || '-'}</p>
                                  </div>
                                  <div className="bg-slate-50 rounded p-1.5 text-center">
                                    <p className="text-slate-500">Stan Skrg</p>
                                    <p className="font-semibold text-slate-700">{bill.detail.stan_sekarang?.toLocaleString('id-ID') || '-'}</p>
                                  </div>
                                  <div className="bg-cyan-50 rounded p-1.5 text-center">
                                    <p className="text-cyan-600">Pakai</p>
                                    <p className="font-bold text-cyan-700">{bill.detail.pakai || '-'}m³</p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      
                      {/* Total Summary for share */}
                      <div className="mt-3 pt-3 border-t border-dashed border-slate-200">
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-slate-600">Total Tagihan ({billsToShare.length} bulan)</span>
                          <span className="font-medium">{formatCurrency(billsToShare.reduce((sum, b) => sum + b.total, 0))}</span>
                        </div>
                        {effectiveAdminFee > 0 && (
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-slate-600">Biaya Admin</span>
                            <span className="font-medium">{formatCurrency(billsToShare.length * effectiveAdminFee)}</span>
                          </div>
                        )}
                        <div className="flex justify-between font-bold text-base pt-1 border-t border-slate-200">
                          <span className="text-slate-800">Total Bayar</span>
                          <span className="text-cyan-600">{formatCurrency(billsToShare.reduce((sum, b) => sum + b.total, 0) + (billsToShare.length * effectiveAdminFee))}</span>
                        </div>
                      </div>
                    </>
                  )}
                  
                  {billsToShare.length === 0 && (
                    <div className="text-center py-4">
                      <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto mb-2" />
                      <p className="text-green-600 font-medium">Semua tagihan sudah lunas</p>
                    </div>
                  )}
                </div>
                
                {/* Visible Customer Info Card */}
                <Card className="glass-card-solid mb-4 border-2 border-cyan-200" data-testid="customer-info">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-cyan-100 flex items-center justify-center flex-shrink-0">
                        <User className="w-6 h-6 text-cyan-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-heading font-bold text-lg text-slate-800" data-testid="customer-name">
                          {pelanggan.nama}
                        </h3>
                        <p className="text-sm text-slate-600 flex items-center gap-1 mt-1">
                          <MapPin className="w-4 h-4" />
                          {pelanggan.alamat}
                        </p>
                        <div className="flex flex-wrap gap-3 mt-3">
                          <span className="px-3 py-1 bg-slate-100 rounded-full text-xs font-medium text-slate-600">
                            ID: {pelanggan.no_sambungan}
                          </span>
                          <span className="px-3 py-1 bg-cyan-100 rounded-full text-xs font-medium text-cyan-700">
                            {pelanggan.golongan}
                          </span>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            pelanggan.status === 'Aktif' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {pelanggan.status}
                          </span>
                        </div>
                        <LocationSaver
                          customerId={pelanggan.no_sambungan}
                          customerName={pelanggan.nama}
                          customerAddress={pelanggan.alamat}
                          source="PDAM_TEBO"
                          onWaNumberChange={setSavedWaNumber}
                        />

                        {isPetugas && !isKasir && (
                          <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl p-3 mt-3" data-testid="admin-fee-toggle-row">
                            <div>
                              <p className="text-sm font-semibold text-slate-800">Biaya Admin</p>
                              <p className="text-xs text-slate-500">
                                {adminEnabled ? `Aktif · ${formatCurrency(adminFee)}/tagihan` : "Nonaktif · tanpa biaya admin (pembayaran di kantor)"}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => setAdminEnabled((v) => !v)}
                              data-testid="admin-fee-toggle-btn"
                              aria-pressed={adminEnabled}
                              className={`relative w-14 h-7 rounded-full transition-colors flex-shrink-0 ${adminEnabled ? "bg-cyan-500" : "bg-slate-300"}`}
                            >
                              <span
                                className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${adminEnabled ? "translate-x-7" : ""}`}
                              />
                            </button>
                          </div>
                        )}

                        {/* Riwayat Pembayaran via aplikasi (sinkron dengan Kelola Transaksi) */}
                        <div className="bg-slate-50 border border-slate-200 rounded-xl mt-3 overflow-hidden" data-testid="payment-history-section">
                          <button
                            type="button"
                            onClick={() => setShowHistory((v) => !v)}
                            className="w-full flex items-center justify-between p-3"
                            data-testid="payment-history-toggle"
                          >
                            <div className="flex items-center gap-2">
                              <History className="w-4 h-4 text-slate-500" />
                              <p className="text-sm font-semibold text-slate-800">
                                Riwayat Pembayaran di Aplikasi ({paymentHistory.length})
                              </p>
                            </div>
                            <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${showHistory ? "rotate-90" : ""}`} />
                          </button>
                          {showHistory && (
                            <div className="px-3 pb-3">
                              {paymentHistory.length === 0 ? (
                                <p className="text-xs text-slate-500 text-center py-2" data-testid="payment-history-empty">
                                  Belum ada pembayaran via aplikasi untuk ID ini.
                                </p>
                              ) : (
                                <div className="space-y-1.5 max-h-64 overflow-y-auto">
                                  {paymentHistory.map((p) => (
                                    <div
                                      key={p.id}
                                      className="flex items-center justify-between gap-2 bg-white border border-slate-100 rounded-lg px-2.5 py-1.5"
                                      data-testid={`payment-history-item-${p.periode}`}
                                    >
                                      <div className="min-w-0">
                                        <p className="text-xs font-semibold text-slate-700">{p.periode_text || p.periode}</p>
                                        <p className="text-[11px] text-slate-500 truncate">
                                          {p.payment_date ? new Date(p.payment_date).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" }) : "-"}
                                          {p.cashier_name ? ` · ${p.cashier_name}` : ""}
                                          {p.payment_method ? ` · ${p.payment_method === "cash" ? "Tunai" : "Transfer"}` : ""}
                                        </p>
                                      </div>
                                      <p className="text-xs font-bold text-emerald-600 flex-shrink-0">{formatCurrency(p.total_bayar || 0)}</p>
                                    </div>
                                  ))}
                                </div>
                              )}
                              <p className="text-[10px] text-slate-400 mt-2 text-center">
                                Data sinkron dengan Kelola Transaksi — jika dihapus di sana, hilang juga di sini.
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Share Bills Buttons */}
                <div className="flex justify-center flex-wrap gap-2 mb-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleShareBills}
                    disabled={isSharing}
                    className="gap-2"
                    data-testid="share-bills-btn"
                  >
                    {isSharing && !shareWaMode ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                    Share Tagihan
                  </Button>
                  <Button
                    size="sm"
                    onClick={openShareWhatsappFlow}
                    disabled={isSharing}
                    className={`gap-2 text-white ${savedWaNumber ? "bg-[#25D366] hover:bg-[#1ebe59]" : "bg-slate-400 hover:bg-slate-500"}`}
                    data-testid="share-bills-wa-btn"
                    title={savedWaNumber ? `Kirim ke +${savedWaNumber}` : "Mohon simpan No. WhatsApp pelanggan terlebih dahulu"}
                  >
                    {isSharing && shareWaMode ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" />}
                    Share Tagihan WhatsApp
                  </Button>
                </div>
                {/* WA app preference indicator */}
                {savedWaNumber && waAppPref !== "ask" && (
                  <div className="flex justify-center mb-4 -mt-2">
                    <button
                      type="button"
                      onClick={() => setShowWaAppPicker(true)}
                      className="text-[11px] text-slate-500 hover:text-cyan-600 underline decoration-dotted"
                      data-testid="change-wa-app-pref"
                    >
                      Aplikasi WA: <span className="font-semibold">{waAppPref === "wa" ? "WhatsApp" : "WhatsApp Business"}</span> · Ubah
                    </button>
                  </div>
                )}
              </motion.div>
            )}

            {/* Info: bills hidden because already paid via app */}
            {!isLoading && pelanggan && localPaidBills.length > 0 && !paymentResult && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.15 }}
                className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4"
                data-testid="local-paid-info"
              >
                <p className="font-semibold text-amber-800 text-sm">
                  {localPaidBills.length} tagihan sudah dibayar via aplikasi
                </p>
                <p className="text-xs text-amber-700 mt-0.5 mb-2">
                  Di server PDAM masih tercatat belum lunas (menunggu penyetoran), sehingga tidak ditampilkan di daftar tagihan.
                </p>
                <div className="space-y-1">
                  {localPaidBills.map((p) => (
                    <div key={p.periode} className="flex items-center justify-between text-xs bg-white/70 border border-amber-100 rounded-lg px-2.5 py-1.5" data-testid={`local-paid-item-${p.periode}`}>
                      <span className="font-medium text-slate-700">{p.periode_text}</span>
                      <span className="text-slate-500">
                        {p.payment_date ? new Date(p.payment_date).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" }) : "-"}
                        {p.cashier_name && p.cashier_name !== "-" ? ` · ${p.cashier_name}` : ""}
                      </span>
                      <span className="font-semibold text-amber-700">{formatCurrency(p.total)}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Bills List */}
            {!isLoading && bills.length > 0 && !paymentResult && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.2 }}
              >
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading font-semibold text-slate-800">
                    Tagihan Belum Dibayar ({bills.length})
                  </h2>
                  {/* Only show select all button for petugas */}
                  {isPetugas && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={selectAll}
                      className="text-xs"
                      data-testid="select-all-btn"
                    >
                      {selectedBills.length === bills.length ? "Batal Pilih" : "Pilih Semua"}
                    </Button>
                  )}
                </div>

                {/* Mode Angsuran info for petugas */}
                {isPetugas && (
                  <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-4 mb-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-cyan-800">Mode Angsuran</p>
                        <p className="text-xs text-cyan-600">Pilih tagihan bulan mana saja yang ingin dibayar</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-3 mb-6" data-testid="bills-list">
                  {bills.map((bill, index) => {
                    const isSelected = isPetugas ? selectedBills.find(b => b.id === bill.id) : true;
                    return (
                      <motion.div
                        key={bill.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                      >
                        <Card 
                          className={`transition-all ${
                            isPetugas ? 'cursor-pointer' : ''
                          } ${
                            isSelected 
                              ? "border-2 border-cyan-500 shadow-lg bg-cyan-50/50" 
                              : "hover:shadow-md hover:border-cyan-200"
                          }`}
                          onClick={() => isPetugas && toggleBillSelection(bill)}
                          data-testid={`bill-card-${bill.periode}`}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                {/* Only show checkbox for petugas */}
                                {isPetugas && (
                                  <div className={`w-6 h-6 rounded-md flex items-center justify-center transition-all ${
                                    isSelected 
                                      ? "bg-cyan-500 text-white" 
                                      : "border-2 border-slate-300 bg-white"
                                  }`}>
                                    {isSelected && <Check className="w-4 h-4" />}
                                  </div>
                                )}
                                <div>
                                  <div className="flex items-center gap-2">
                                    <Calendar className="w-4 h-4 text-slate-400" />
                                    <span className="font-semibold text-slate-800">{bill.periode_text}</span>
                                  </div>
                                  <p className="text-xs text-slate-500 mt-1">
                                    Periode: {bill.periode}
                                  </p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-lg text-cyan-600">
                                  {formatCurrency(bill.total)}
                                </p>
                                {effectiveAdminFee > 0 && (
                                  <p className="text-xs text-slate-500">
                                    + Admin {formatCurrency(effectiveAdminFee)}
                                  </p>
                                )}
                              </div>
                            </div>
                            
                            {/* Detail Stan Meter */}
                            {bill.detail && (
                              <div className="mt-3 pt-3 border-t border-slate-200" data-testid={`bill-detail-${bill.periode}`}>
                                <div className="grid grid-cols-3 gap-2 text-sm">
                                  <div className="bg-slate-50 rounded-lg p-2 text-center">
                                    <p className="text-xs text-slate-500">Stan Bulan Lalu</p>
                                    <p className="font-semibold text-slate-800">{bill.detail.stan_lalu?.toLocaleString('id-ID') || '-'}</p>
                                  </div>
                                  <div className="bg-slate-50 rounded-lg p-2 text-center">
                                    <p className="text-xs text-slate-500">Stan Sekarang</p>
                                    <p className="font-semibold text-slate-800">{bill.detail.stan_sekarang?.toLocaleString('id-ID') || '-'}</p>
                                  </div>
                                  <div className="bg-cyan-50 rounded-lg p-2 text-center">
                                    <p className="text-xs text-cyan-600">Pakai (m³)</p>
                                    <p className="font-bold text-cyan-700">{bill.detail.pakai?.toLocaleString('id-ID') || '-'}m³</p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </motion.div>
                    );
                  })}
                </div>

                {/* Payment Summary */}
                {billsToPay.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Card className="glass-card border-2 border-cyan-200">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center">
                            <CreditCard className="w-5 h-5 text-cyan-600" />
                          </div>
                          <div>
                            <h3 className="font-heading font-semibold text-slate-800">
                              Ringkasan Pembayaran
                            </h3>
                            <p className="text-sm text-slate-500">
                              {isPetugas 
                                ? `${billsToPay.length} dari ${bills.length} tagihan dipilih`
                                : `${billsToPay.length} tagihan harus dibayar sekaligus`
                              }
                            </p>
                          </div>
                        </div>

                        <div className="bg-slate-50 rounded-xl p-4 mb-4 space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-600">Total Tagihan</span>
                            <span className="font-medium">{formatCurrency(totalTagihan)}</span>
                          </div>
                          {effectiveAdminFee > 0 && (
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-600">Admin ({billsToPay.length} x {formatCurrency(effectiveAdminFee)})</span>
                              <span className="font-medium">{formatCurrency(totalAdminFee)}</span>
                            </div>
                          )}
                          <div className="border-t border-slate-200 pt-2 flex justify-between">
                            <span className="font-semibold text-slate-800">Total Bayar</span>
                            <span className="text-2xl font-heading font-bold text-cyan-600">
                              {formatCurrency(totalBayar)}
                            </span>
                          </div>
                        </div>

                        {isReadOnly ? (
                          <div className="w-full h-14 rounded-full text-base font-semibold bg-rose-50 text-rose-700 border-2 border-rose-200 flex items-center justify-center gap-2" data-testid="readonly-info">
                            <Lock className="w-5 h-5" />
                            Mode Lihat Saja (Petugas Lapangan)
                          </div>
                        ) : (
                          <Button
                            onClick={handlePayment}
                            disabled={isProcessing || !isLoggedIn}
                            className="w-full h-14 rounded-full text-lg font-semibold bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
                            data-testid="pay-btn"
                          >
                            {isProcessing ? (
                              <>
                                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                                Memproses...
                              </>
                            ) : !isLoggedIn ? (
                              "Login untuk Membayar"
                            ) : (
                              <>
                                <CreditCard className="w-5 h-5 mr-2" />
                                Bayar Sekarang
                              </>
                            )}
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                )}
              </motion.div>
            )}

            {/* No Bills - Simplified */}
            {!isLoading && pelanggan && bills.length === 0 && !paymentResult && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <Card className="glass-card-solid">
                  <CardContent className="p-12 text-center">
                    <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <h2 className="text-xl font-heading font-semibold text-slate-800 mb-2">
                      Tidak Ada Tagihan
                    </h2>
                    <p className="text-slate-600">
                      Semua tagihan sudah lunas. Terima kasih!
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            )}

{/* Info card removed as per user request */}
          </div>
        </motion.div>
      </main>
    </div>
  );
}
